import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  ViewEncapsulation
} from '@angular/core';

@Component({
  selector: 'app-tooltip',
  standalone: false,
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TooltipComponent implements OnDestroy {
  @Input() text = '';

  private overlayEl: HTMLElement | null = null;

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.showTooltip();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hideTooltip();
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this.showTooltip();
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this.hideTooltip();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChanged(): void {
    this.positionTooltip();
  }

  ngOnDestroy(): void {
    this.hideTooltip();
  }

  private showTooltip(): void {
    if (!isPlatformBrowser(this.platformId) || !this.text?.trim()) return;
    if (this.overlayEl) {
      this.positionTooltip();
      return;
    }

    const overlay = this.renderer.createElement('span') as HTMLElement;
    this.renderer.addClass(overlay, 'app-tooltip-overlay');
    this.renderer.setAttribute(overlay, 'role', 'tooltip');
    this.renderer.setProperty(overlay, 'textContent', this.text);
    this.renderer.appendChild(this.document.body, overlay);
    this.overlayEl = overlay;
    this.positionTooltip();
  }

  private hideTooltip(): void {
    if (!this.overlayEl) return;
    this.renderer.removeChild(this.document.body, this.overlayEl);
    this.overlayEl = null;
  }

  private positionTooltip(): void {
    if (!this.overlayEl || !isPlatformBrowser(this.platformId)) return;

    const spacing = 12;
    const viewportPadding = 8;
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const tooltipRect = this.overlayEl.getBoundingClientRect();

    let left = hostRect.left + hostRect.width / 2 - tooltipRect.width / 2;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding));

    let top = hostRect.top - tooltipRect.height - spacing;
    let placement: 'top' | 'bottom' = 'top';
    if (top < viewportPadding) {
      top = hostRect.bottom + spacing;
      placement = 'bottom';
    }

    this.overlayEl.classList.remove('is-top', 'is-bottom');
    this.overlayEl.classList.add(placement === 'top' ? 'is-top' : 'is-bottom');
    this.renderer.setStyle(this.overlayEl, 'left', `${left + window.scrollX}px`);
    this.renderer.setStyle(this.overlayEl, 'top', `${top + window.scrollY}px`);
  }
}
